import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Globe, ArrowLeft } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const COMPANY_CONFIG = {
  lpc: {
    id: 'La Piscine Container',
    name: 'La Piscine Container',
    subtitle: 'Gestion de Chantier',
    logo: '/logo-piscine-white.png',
    bg: '/bg-piscine.jpg',
    overlay: 'rgba(14,33,71,0.75)',
    overlayEnd: 'rgba(14,33,71,0.92)',
    accent: '#F59E0B',
  },
  ebc: {
    id: 'Eco Bati Construction',
    name: 'Eco Bati Construction',
    subtitle: 'Gestion de Chantier',
    logo: '/logo-ecobati.png',
    bg: '/bg-ecobati.jpg',
    overlay: 'rgba(30,40,50,0.7)',
    overlayEnd: 'rgba(30,40,50,0.9)',
    accent: '#64B5A0',
  },
};

export default function LoginPage() {
  const { login, register } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'employee' });

  const languageFlags = { fr: '\u{1F1EB}\u{1F1F7}', ru: '\u{1F1F7}\u{1F1FA}', md: '\u{1F1F2}\u{1F1E9}', uk: '\u{1F1FA}\u{1F1E6}' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const email = form.email.trim().toLowerCase();
    const password = form.password.trim();
    try {
      if (isLogin) {
        await login(email, password);
        toast.success(t('loginSuccess'));
      } else {
        if (!form.name.trim()) {
          toast.error(t('nameRequired'));
          setLoading(false);
          return;
        }
        try {
          await register(email, password, form.name.trim(), COMPANY_CONFIG[selectedCompany].id, form.role);
        } catch (regError) {
          if (regError.response?.status === 202) {
            // Account created but pending approval
            toast.success({ fr: 'Compte créé ! En attente d\'approbation par votre manager.', ru: 'Аккаунт создан! Ожидает одобрения менеджера.', md: 'Cont creat! În așteptarea aprobării managerului.', uk: 'Обліковий запис створено! Очікує схвалення менеджера.' }[language] || 'Compte créé ! En attente d\'approbation.', { duration: 8000 });
            setIsLogin(true);
            setLoading(false);
            return;
          }
          throw regError;
        }
      }
    } catch (error) {
      let message;
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.response?.status === 401) {
        message = { fr: 'Email ou mot de passe incorrect', ru: 'Неверный email или пароль', md: 'Email sau parolă incorectă', uk: 'Невірний email або пароль' }[language] || 'Email ou mot de passe incorrect';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = { fr: 'Le serveur met trop de temps à répondre. Réessayez.', ru: 'Сервер слишком долго отвечает. Повторите.', md: 'Serverul răspunde prea lent. Reîncercați.', uk: 'Сервер надто довго відповідає. Повторіть.' }[language] || 'Le serveur met trop de temps à répondre.';
      } else if (!error.response) {
        message = { fr: 'Impossible de joindre le serveur. Réessayez dans quelques instants.', ru: 'Не удалось связаться с сервером. Повторите через некоторое время.', md: 'Imposibil de contactat serverul. Reîncercați.', uk: 'Неможливо зв\'язатися з сервером. Повторіть.' }[language] || 'Impossible de joindre le serveur.';
      } else {
        message = { fr: `Erreur serveur (${error.response.status})`, ru: `Ошибка сервера (${error.response.status})`, md: `Eroare server (${error.response.status})`, uk: `Помилка сервера (${error.response.status})` }[language] || `Erreur serveur (${error.response.status})`;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // --- Company Selection Screen ---
  if (!selectedCompany) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex flex-col relative" style={{ backgroundColor: '#0f1923' }}>
        {/* Language Selector */}
        <div className="absolute top-4 right-4 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-colors text-white">
              <Globe className="w-4 h-4" />
              <span className="text-lg">{languageFlags[language]}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage('fr')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.fr}</span> {t('french')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('ru')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.ru}</span> {t('russian')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('md')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.md}</span> {t('moldovan')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('uk')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.uk}</span> {t('ukrainian')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Title */}
        <div className="text-center pt-16 pb-4 px-4 z-10">
          <p className="text-white/50 text-sm uppercase tracking-widest mb-2">
            {{ fr: 'Choisissez votre entreprise', ru: 'Vyberi kompaniyu', md: 'Alegeti compania', uk: 'Oberit kompaniyu' }[language] || 'Choisissez votre entreprise'}
          </p>
        </div>

        {/* Company Cards */}
        <div className="flex-1 flex flex-col lg:flex-row gap-0 relative z-10">
          {/* La Piscine Container */}
          <button
            onClick={() => setSelectedCompany('lpc')}
            className="flex-1 relative overflow-hidden group cursor-pointer border-0 bg-transparent p-0"
            data-testid="select-company-lpc"
          >
            <div className="absolute inset-0">
              <img src="/bg-piscine.jpg" alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 transition-opacity duration-500" style={{ background: 'linear-gradient(180deg, rgba(14,33,71,0.5) 0%, rgba(14,33,71,0.85) 100%)' }} />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'linear-gradient(180deg, rgba(14,33,71,0.3) 0%, rgba(14,33,71,0.7) 100%)' }} />
            </div>
            <div className="relative z-10 flex flex-col items-center justify-center h-full min-h-[40vh] lg:min-h-0 p-8">
              <img src="/logo-piscine-white.png" alt="La Piscine Container" className="w-20 h-20 lg:w-28 lg:h-28 object-contain mb-6 drop-shadow-lg transition-transform duration-500 group-hover:scale-110" />
              <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-2">La Piscine Container</h2>
              <p className="text-white/50 text-sm">Gestion de Chantier</p>
              <div className="mt-6 px-8 py-2.5 rounded-full border-2 border-white/30 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0" style={{ backgroundColor: 'rgba(245,158,11,0.3)' }}>
                {{ fr: 'Accéder', ru: 'Войти', md: 'Accesare', uk: 'Увійти' }[language] || 'Accéder'}
              </div>
            </div>
          </button>

          {/* Divider */}
          <div className="hidden lg:flex items-center justify-center relative z-10">
            <div className="w-px h-2/3 bg-white/15" />
          </div>
          <div className="lg:hidden flex justify-center relative z-10">
            <div className="h-px w-2/3 bg-white/15" />
          </div>

          {/* Eco Bati Construction */}
          <button
            onClick={() => setSelectedCompany('ebc')}
            className="flex-1 relative overflow-hidden group cursor-pointer border-0 bg-transparent p-0"
            data-testid="select-company-ebc"
          >
            <div className="absolute inset-0">
              <img src="/bg-ecobati.jpg" alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 transition-opacity duration-500" style={{ background: 'linear-gradient(180deg, rgba(30,40,50,0.5) 0%, rgba(30,40,50,0.85) 100%)' }} />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'linear-gradient(180deg, rgba(30,40,50,0.3) 0%, rgba(30,40,50,0.7) 100%)' }} />
            </div>
            <div className="relative z-10 flex flex-col items-center justify-center h-full min-h-[40vh] lg:min-h-0 p-8">
              <div className="bg-white rounded-2xl p-3 mb-6 shadow-lg transition-transform duration-500 group-hover:scale-110">
                <img src="/logo-ecobati.png" alt="Eco Bati Construction" className="w-16 h-16 lg:w-24 lg:h-24 object-contain" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-2">Eco Bati Construction</h2>
              <p className="text-white/50 text-sm">Gestion de Chantier</p>
              <div className="mt-6 px-8 py-2.5 rounded-full border-2 border-white/30 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0" style={{ backgroundColor: 'rgba(100,181,160,0.3)' }}>
                {{ fr: 'Accéder', ru: 'Войти', md: 'Accesare', uk: 'Увійти' }[language] || 'Accéder'}
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // --- Login / Register Form ---
  const company = COMPANY_CONFIG[selectedCompany];

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col lg:flex-row">
      {/* Left side - Company Hero */}
      <div
        className="lg:w-1/2 relative overflow-hidden flex items-center justify-center p-8 lg:p-12"
        style={{
          backgroundImage: `linear-gradient(180deg, ${company.overlay} 0%, ${company.overlayEnd} 100%), url(${company.bg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10 text-center lg:text-left max-w-lg">
          <div className="flex items-center gap-4 mb-6 justify-center lg:justify-start">
            {selectedCompany === 'ebc' ? (
              <div className="bg-white rounded-2xl p-2">
                <img src={company.logo} alt={company.name} className="w-14 h-14 lg:w-16 lg:h-16 object-contain" />
              </div>
            ) : (
              <img src={company.logo} alt={company.name} className="w-14 h-14 lg:w-16 lg:h-16 object-contain" />
            )}
            <h1 className="text-2xl lg:text-4xl font-bold text-white tracking-tight">{company.name}</h1>
          </div>
          <p className="text-lg text-white/70 mb-8 leading-relaxed">{company.subtitle}</p>
          <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
            <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-xl">
              <span className="font-bold text-2xl" style={{ color: company.accent }}>100%</span>
              <span className="text-white/60 text-sm ml-2">Mobile</span>
            </div>
            <div className="bg-white/10 backdrop-blur px-4 py-2 rounded-xl">
              <span className="font-bold text-2xl" style={{ color: company.accent }}>Simple</span>
              <span className="text-white/60 text-sm ml-2">& Efficace</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-12 relative" style={{ backgroundColor: 'var(--color-bg)' }}>
        {/* Language Selector */}
        <div className="absolute top-4 right-4">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 bg-white border rounded-xl hover:bg-gray-50 transition-colors" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}>
              <Globe className="w-4 h-4" />
              <span className="text-lg">{languageFlags[language]}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage('fr')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.fr}</span> {t('french')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('ru')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.ru}</span> {t('russian')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('md')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.md}</span> {t('moldovan')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('uk')} className="cursor-pointer">
                <span className="mr-2">{languageFlags.uk}</span> {t('ukrainian')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="w-full max-w-md">
          <div className="card-base p-8">
            {/* Back button */}
            <button
              onClick={() => { setSelectedCompany(null); setIsLogin(true); setForm({ email: '', password: '', name: '' }); }}
              className="flex items-center gap-2 text-sm mb-6 hover:underline"
              style={{ color: 'var(--color-primary)' }}
              data-testid="back-to-company-select"
            >
              <ArrowLeft className="w-4 h-4" />
              {{ fr: 'Changer d\'entreprise', ru: 'Smenit\' kompaniyu', md: 'Schimba compania', uk: 'Zminyty kompaniyu' }[language] || 'Changer d\'entreprise'}
            </button>

            <h2 className="text-2xl font-bold mb-2 tracking-tight" style={{ color: 'var(--color-primary)' }}>
              {isLogin ? t('login') : t('register')}
            </h2>
            <p className="mb-8" style={{ color: 'var(--color-text)', opacity: 0.6 }}>
              {isLogin ? t('loginSubtitle') : t('registerSubtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <>
                <div className="form-group animate-slideUp">
                  <Label htmlFor="name" className="form-label">{t('fullName')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-base"
                    placeholder="Jean Dupont"
                    data-testid="register-name-input"
                  />
                </div>

                {/* Role selector */}
                <div className="form-group">
                  <Label className="form-label">Statut</Label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="employee">Employé</option>
                    <option value="subcontractor">Sous-traitant</option>
                  </select>
                </div>
                </>
              )}

              <div className="form-group">
                <Label htmlFor="email" className="form-label">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-base"
                  placeholder="jean@entreprise.fr"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="password" className="form-label">{t('password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="input-base pr-12"
                    placeholder="********"
                    required
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full h-12"
                data-testid="submit-btn"
              >
                {loading ? <span className="spinner" /> : isLogin ? t('loginButton') : t('registerButton')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm transition-colors hover:underline"
                style={{ color: 'var(--color-primary)' }}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? t('noAccount') : t('hasAccount')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
